import { useState, useEffect } from 'react';
import { X, Copy } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface CustomerFormData {
  customer_number: string;
  name: string;
  mc_number: string;
  dot_number: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_phone_ext: string;
  billing_email: string;
  billing_phone: string;
  billing_phone_ext: string;
  billing_street: string;
  billing_city: string;
  billing_state: string;
  billing_zip: string;
  transfer_documents: boolean;
  email_notified: boolean;
  transfer_to_factoring: boolean;
  factoring_company: string;
  notes: string;
}

interface CustomerFormProps {
  customerId?: string;
  onClose: () => void;
  onSuccess: () => void;
  isModal?: boolean;
}

export function CustomerForm({ customerId, onClose, onSuccess, isModal = true }: CustomerFormProps) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!customerId);
  const [factoringCompanies, setFactoringCompanies] = useState<Array<{ id: string; name: string }>>([]);
  
  const [formData, setFormData] = useState<CustomerFormData>({
    customer_number: `C-${Date.now()}`,
    name: '',
    mc_number: '',
    dot_number: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_phone_ext: '',
    billing_email: '',
    billing_phone: '',
    billing_phone_ext: '',
    billing_street: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    transfer_documents: true,
    email_notified: true,
    transfer_to_factoring: true,
    factoring_company: '',
    notes: '',
  });

  useEffect(() => {
    loadFactoringCompanies();
    if (customerId) {
      loadCustomer();
    }
  }, [customerId]);

  async function loadFactoringCompanies() {
    try {
      const { data, error } = await supabase
        .from('factoring_companies')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      setFactoringCompanies(data || []);
    } catch (error) {
      console.error('Error loading factoring companies:', error);
    }
  }

  async function loadCustomer() {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .single();

      if (error) throw error;
      
      if (data) {
        setFormData({
          customer_number: data.customer_number || '',
          name: data.name || '',
          mc_number: data.mc_number || '',
          dot_number: data.dot_number || '',
          contact_name: data.contact_name || '',
          contact_email: data.contact_email || '',
          contact_phone: data.contact_phone || '',
          contact_phone_ext: data.contact_phone_ext || '',
          billing_email: data.billing_email || '',
          billing_phone: data.billing_phone || '',
          billing_phone_ext: data.billing_phone_ext || '',
          billing_street: data.billing_street || '',
          billing_city: data.billing_city || '',
          billing_state: data.billing_state || '',
          billing_zip: data.billing_zip || '',
          transfer_documents: data.transfer_documents ?? true,
          email_notified: data.email_notified ?? true,
          transfer_to_factoring: data.transfer_to_factoring ?? true,
          factoring_company: data.factoring_company || '',
          notes: data.notes || '',
        });
      }
    } catch (error) {
      console.error('Error loading customer:', error);
      toast.error('Failed to load customer');
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.mc_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);

    try {
      if (customerId) {
        // Update existing customer
        const { error } = await supabase
          .from('customers')
          .update({
            ...formData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', customerId);

        if (error) throw error;
        toast.success('Customer updated successfully!');
      } else {
        // Create new customer
        const { error } = await supabase
          .from('customers')
          .insert([formData]);

        if (error) throw error;
        toast.success('Customer added successfully!');
      }
      
      onSuccess();
    } catch (error: any) {
      console.error('Error saving customer:', error);
      toast.error(error.message || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const copyContactDetails = () => {
    setFormData({
      ...formData,
      billing_email: formData.contact_email,
      billing_phone: formData.contact_phone,
      billing_phone_ext: formData.contact_phone_ext,
    });
    toast.success('Contact details copied to billing contact');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const formContent = (
    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              MC/MX/FF Number *
            </label>
            <input
              type="text"
              value={formData.mc_number}
              onChange={(e) => setFormData({ ...formData, mc_number: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              DOT Number
            </label>
            <input
              type="text"
              value={formData.dot_number}
              onChange={(e) => setFormData({ ...formData, dot_number: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Contact Name
            </label>
            <input
              type="text"
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Phone Number
            </label>
            <div className="flex gap-2">
              <input
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="text"
                placeholder="ext"
                value={formData.contact_phone_ext}
                onChange={(e) => setFormData({ ...formData, contact_phone_ext: e.target.value })}
                className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Copy Button */}
      <div className="flex justify-start">
        <button
          type="button"
          onClick={copyContactDetails}
          className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
        >
          <Copy className="w-4 h-4" />
          Copy contact details
        </button>
      </div>

      {/* Billing Contact */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Billing Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              value={formData.billing_email}
              onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Phone Number
            </label>
            <div className="flex gap-2">
              <input
                type="tel"
                value={formData.billing_phone}
                onChange={(e) => setFormData({ ...formData, billing_phone: e.target.value })}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="text"
                placeholder="ext"
                value={formData.billing_phone_ext}
                onChange={(e) => setFormData({ ...formData, billing_phone_ext: e.target.value })}
                className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Billing Address */}
        <div className="space-y-4 pt-2">
          <h4 className="text-md font-medium text-slate-800">Billing Address</h4>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Street
              </label>
              <input
                type="text"
                value={formData.billing_street}
                onChange={(e) => setFormData({ ...formData, billing_street: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  value={formData.billing_city}
                  onChange={(e) => setFormData({ ...formData, billing_city: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  State *
                </label>
                <input
                  type="text"
                  value={formData.billing_state}
                  onChange={(e) => setFormData({ ...formData, billing_state: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Zip Code *
                </label>
                <input
                  type="text"
                  value={formData.billing_zip}
                  onChange={(e) => setFormData({ ...formData, billing_zip: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Load Billing Preferences */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Load Billing Preferences</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={formData.transfer_documents}
              onChange={(e) => setFormData({ ...formData, transfer_documents: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-slate-700">Transfer Documents</span>
          </label>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={formData.email_notified}
              onChange={(e) => setFormData({ ...formData, email_notified: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-slate-700">Send documents by email</span>
          </label>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={formData.transfer_to_factoring}
              onChange={(e) => setFormData({ ...formData, transfer_to_factoring: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-slate-700">Use factoring company</span>
          </label>

          {formData.transfer_to_factoring && (
            <div className="ml-7 mt-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Factoring Company *
              </label>
              <select
                value={formData.factoring_company}
                onChange={(e) => setFormData({ ...formData, factoring_company: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a factoring company</option>
                {factoringCompanies.map((company) => (
                  <option key={company.id} value={company.name}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Notes</h3>
        <div>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            maxLength={2000}
            rows={4}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Add any additional notes about this customer..."
          />
          <p className="text-xs text-slate-500 mt-1">
            {formData.notes.length}/2000 characters
          </p>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : customerId ? 'Update Customer' : 'Add Customer'}
        </button>
      </div>
    </form>
  );

  if (!isModal) {
    return formContent;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div>
            <h2 className="text-2xl font-bold">
              {customerId ? 'Edit Customer' : 'Add Customer'}
            </h2>
            <p className="text-sm text-blue-100 mt-1">
              {customerId ? 'Update customer information' : 'Enter customer information'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {formContent}
      </div>
    </div>
  );
}
